import { useState } from "react";
import { API } from '../../config/api';
import axios from "axios";
import { parseBlobError } from "../../utils/parseBlobError";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { SubmissionLayout, StatusBadge } from "../../components/SubmissionLayout";
import BonsaiMate from "../../components/BonsaiMate";
import { FileText, Download, Upload, Loader2, CheckCircle2, AlertTriangle, ExternalLink, Banknote, Building2, Landmark, Clock, CreditCard } from "lucide-react";

function FinancialRow({ label, value, bold = false, accent = false }) {
  return (
    <div className={`flex justify-between items-center py-2 border-b border-white/10/40 last:border-0 ${bold ? 'font-semibold' : ''}`}>
      <span className="text-sm text-txt-faint">{label}</span>
      <span className={`text-sm tabular-nums ${accent ? 'font-medium text-teal' : 'font-medium text-txt-faint'}`}>{value}</span>
    </div>
  );
}

export default function AccountsInstructionDetail({ submission, formatDate, navigate, isDirector, onDelete }) {
  const [actioning, setActioning] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [status, setStatus] = useState(submission?.status || "pending");
  const [docGenerated, setDocGenerated] = useState(!!submission?.document_generated);

  const data = submission?.data || {};
  const header = data.header || {};
  const funds = data.funds_summary || {};
  const bank = data.client_bank_details || {};
  const timing = data.timing_instructions || {};
  const payments = data.payment_breakdown || [];
  const isUrgent = timing.urgent;

  const fmt = (v) =>
    new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", minimumFractionDigits: 2 }).format(parseFloat(v) || 0);

  const directionLabel = {
    BalanceRequiredFromClient: "Balance required from client",
    BalanceDueToClient: "Balance due to client",
    Balanced: "Balanced (nil due)",
  }[funds.net_direction] || funds.net_direction || "\u2014";

  const totalPayments = payments.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);

  const handleMarkActioned = async () => {
    setActioning(true);
    try {
      const res = await axios.patch(`${API}/accountsinstruction/${submission.id}/action`);
      if (res.data?.success) { setStatus("actioned"); toast.success("Marked as actioned"); }
    } catch { toast.error("Failed to mark as actioned"); }
    finally { setActioning(false); }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/accountsinstruction/${submission.id}/generate`);
      if (res.data?.success) {
        setDocGenerated(true);
        let msg = "Document generated.";
        if (res.data.sharepoint?.uploaded) msg += " Filed to SharePoint.";
        if (res.data.email?.status === "sent") msg += " Email sent to Accounts.";
        toast.success(msg);
      }
    } catch { toast.error("Document generation failed"); }
    finally { setGenerating(false); }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API}/accountsinstruction/${submission.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url; link.setAttribute("download", `Accounts_Instruction_${header.matterref || "AI"}.docx`);
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const detail = await parseBlobError(err);
      toast.error(`Download failed: ${detail}`);
    }
    finally { setDownloading(false); }
  };

  const actions = (
    <>
      <Button variant="outline" size="sm" onClick={handleDownload} disabled={downloading}
        className="border-navy-border text-teal hover:bg-teal/5 gap-1 text-xs h-8" data-testid="ai-download-btn">
        {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Download
      </Button>
      <Button size="sm" onClick={handleGenerate} disabled={generating}
        className={`gap-1 text-xs h-8 ${docGenerated ? "bg-teal hover:bg-teal" : "bg-teal hover:bg-teal-hover"} text-white`}
        data-testid="ai-generate-btn">
        {generating ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating...</>
          : docGenerated ? <><CheckCircle2 className="w-3.5 h-3.5" /> Re-generate</>
          : <><Upload className="w-3.5 h-3.5" /> Generate &amp; File</>}
      </Button>
      {status === "pending" && (
        <Button variant="outline" size="sm" onClick={handleMarkActioned} disabled={actioning}
          className="border-teal text-teal hover:bg-teal-dim gap-1 text-xs h-8" data-testid="ai-mark-actioned-btn">
          {actioning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Actioned
        </Button>
      )}
    </>
  );

  return (
    <>
    <SubmissionLayout
      navigate={navigate}
      title={header.matterref || "Completion Funds Request"}
      subtitle={`${header.client_name || ""} \u2014 ${header.matter_description || ""}`}
      matterReference={header.matterref}
      badge="Completion Funds"
      badgeColor="indigo"
      badgeIcon={Banknote}
      isTest={false}
      isDirector={isDirector}
      onDelete={onDelete}
      actions={actions}
    >
      {/* Status badges */}
      <div className="flex items-center justify-between bg-white/80 backdrop-blur-sm rounded-md border border-teal/10 px-5 py-3.5 shadow-sm" data-testid="ai-header-info">
        <div className="flex items-center gap-3 flex-wrap">
          <span className={`text-xs px-2.5 py-1 rounded-md font-medium border ${
            status === "actioned" ? "bg-teal-dim text-teal border-teal" : "bg-status-amber-dim text-status-amber border-amber-200"
          }`} data-testid="ai-status-badge">
            {status === "actioned" ? "Actioned" : "Pending"}
          </span>
          {isUrgent && (
            <span className="text-xs px-2.5 py-1 rounded-md font-medium bg-status-red-dim text-status-red border border-red-200 flex items-center gap-1" data-testid="ai-urgent-badge">
              <AlertTriangle className="w-3 h-3" /> URGENT
            </span>
          )}
          {docGenerated && (
            <span className="text-xs px-2.5 py-1 rounded-md font-medium bg-blue-50 text-blue-700 border border-blue-200 flex items-center gap-1" data-testid="ai-doc-badge">
              <FileText className="w-3 h-3" /> Document Generated
              {submission?.sharepoint_web_url && (
                <a href={submission.sharepoint_web_url} target="_blank" rel="noopener noreferrer" className="ml-1 underline">
                  SharePoint <ExternalLink className="inline h-3 w-3" />
                </a>
              )}
            </span>
          )}
          <span className="text-sm text-txt-faint">Submitted {formatDate(submission.created_at)}</span>
        </div>
        <span className="text-xs text-txt-muted font-mono tracking-wide">{submission.id?.slice(0, 8)}</span>
      </div>

      {/* Funds Hero */}
      <div className="bg-gradient-to-r from-teal/5 to-teal/10 rounded-md border-2 border-teal/20 p-6 text-center" data-testid="ai-funds-hero">
        <div className="text-3xl font-bold text-teal">{fmt(funds.net_completion_balance)}</div>
        <div className="text-teal/80 mt-1">{directionLabel}</div>
      </div>

      {/* Bento Grid */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4" data-testid="ai-bento-grid">

        {/* Matter Details — 6 cols */}
        <div className="md:col-span-6 bg-white/80 backdrop-blur-sm rounded-md border border-teal/10 shadow-sm overflow-hidden" data-testid="section-matter-details">
          <div className="px-5 py-4 border-b border-white/10/60 bg-[color:var(--surface-inset)]">
            <h2 className="font-semibold text-teal flex items-center gap-2 text-sm font-sans">
              <Building2 className="w-4 h-4" /> Matter Details
            </h2>
          </div>
          <div className="p-5 space-y-1">
            <FinancialRow label="Matter Ref" value={header.matterref || "\u2014"} accent />
            <FinancialRow label="Client" value={header.client_name || "\u2014"} />
            <FinancialRow label="Description" value={header.matter_description || "\u2014"} />
            <FinancialRow label="Fee Earner" value={header.fee_earner || "\u2014"} />
            <FinancialRow label="Completion Date" value={header.completion_date || "\u2014"} />
          </div>
        </div>

        {/* Bank Details — 6 cols */}
        <div className="md:col-span-6 bg-white/80 backdrop-blur-sm rounded-md border border-teal/10 shadow-sm overflow-hidden" data-testid="section-bank-details">
          <div className="px-5 py-4 border-b border-white/10/60 bg-[color:var(--surface-inset)]">
            <h2 className="font-semibold text-teal flex items-center gap-2 text-sm font-sans">
              <Landmark className="w-4 h-4" /> Client Bank Details
            </h2>
          </div>
          <div className="p-5 space-y-1">
            <FinancialRow label="Account Name" value={bank.account_name || "\u2014"} />
            <FinancialRow label="Sort Code" value={bank.sort_code || "\u2014"} accent />
            <FinancialRow label="Account Number" value={bank.account_number || "\u2014"} accent />
            <FinancialRow label="Bank Name" value={bank.bank_name || "\u2014"} />
            <FinancialRow label="Reference" value={bank.reference || "\u2014"} />
          </div>
        </div>

        {/* Funds Summary — 6 cols */}
        <div className="md:col-span-6 bg-white/80 backdrop-blur-sm rounded-md border border-teal/10 shadow-sm overflow-hidden" data-testid="section-funds-summary">
          <div className="px-5 py-4 border-b border-white/10/60 bg-[color:var(--surface-inset)]">
            <h2 className="font-semibold text-teal flex items-center gap-2 text-sm font-sans">
              <Banknote className="w-4 h-4" /> Funds Summary
            </h2>
          </div>
          <div className="p-5 space-y-1">
            <FinancialRow label="Net Completion Balance" value={fmt(funds.net_completion_balance)} bold />
            <FinancialRow label="Direction" value={directionLabel} />
            <FinancialRow label="Total Due from Client" value={fmt(funds.total_due_from_client)} accent />
            <FinancialRow label="Total Due to Client" value={fmt(funds.total_due_to_client)} accent />
          </div>
        </div>

        {/* Timing — 6 cols */}
        <div className="md:col-span-6 bg-white/80 backdrop-blur-sm rounded-md border border-teal/10 shadow-sm overflow-hidden" data-testid="section-timing">
          <div className="px-5 py-4 border-b border-white/10/60 bg-[color:var(--surface-inset)]">
            <h2 className="font-semibold text-teal flex items-center gap-2 text-sm font-sans">
              <Clock className="w-4 h-4" /> Timing Instructions
            </h2>
          </div>
          <div className="p-5 space-y-1">
            <FinancialRow label="Completion Time" value={timing.completion_time || "\u2014"} />
            <FinancialRow label="Funds Required By" value={timing.funds_required_by || "\u2014"} accent />
            {timing.special_instructions && (
              <div className="mt-3 p-3.5 bg-[color:var(--surface-inset)] rounded-md text-sm text-txt-faint border border-white/10/60">
                <div className="font-medium text-txt-faint mb-1 text-xs uppercase tracking-wider">Special Instructions</div>
                {timing.special_instructions}
              </div>
            )}
          </div>
        </div>

        {/* Payment Breakdown — 12 cols */}
        {payments.length > 0 && (
          <div className="md:col-span-12 bg-white/80 backdrop-blur-sm rounded-md border border-teal/10 shadow-sm overflow-hidden" data-testid="section-payment-breakdown">
            <div className="px-5 py-4 border-b border-white/10/60 bg-[color:var(--surface-inset)]">
              <h2 className="font-semibold text-teal flex items-center gap-2 text-sm font-sans">
                <CreditCard className="w-4 h-4" /> Payment Breakdown
              </h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[color:var(--surface-inset)] text-left text-xs font-medium text-txt-faint uppercase tracking-wider">
                    <th className="px-5 py-3">Description</th>
                    <th className="px-5 py-3 text-right">Amount</th>
                    <th className="px-5 py-3">Payee</th>
                    <th className="px-5 py-3">Bank Details</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-navy-border/60">
                  {payments.map((p, i) => (
                    <tr key={i}>
                      <td className="px-5 py-3 text-txt-faint">{p.description}</td>
                      <td className="px-5 py-3 text-right tabular-nums font-medium text-txt-faint">{fmt(p.amount)}</td>
                      <td className="px-5 py-3 text-txt-faint">{p.payee || "\u2014"}</td>
                      <td className="px-5 py-3 text-xs text-txt-muted">{p.payee_bank_details || "\u2014"}</td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-teal/20">
                    <td className="px-5 py-3 font-semibold text-teal">Total</td>
                    <td className="px-5 py-3 text-right tabular-nums font-bold text-teal">{fmt(totalPayments)}</td>
                    <td colSpan={2}></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </SubmissionLayout>
    {header.matterref && (
      <BonsaiMate context="matter" matterReference={header.matterref} />
    )}
    </>
  );
}
